import { zodResolver } from '@hookform/resolvers/zod';
import { Mapping } from 'api/MappingsAPI';
import { FieldType } from 'modules/domains';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

type SchemaField = { fieldName: string; fieldType: FieldType };

const createSchema = (fields: SchemaField[], parentName?: string) => {
  return z.object({
    description: z.string().max(100, 'Description must be 100 characters or less'),
    fieldName: z
      .string()
      .min(1, 'Field is required')
      .max(100, 'Field name must be 100 characters or less')
      .refine((str) => {
        const formattedString = parentName ? `${parentName}.${str}` : str;
        return !fields.some((field) => field.fieldName === formattedString);
      }, 'A field with this name already exists')
      .refine((str) => {
        const hasParent = str.includes('.');
        if (!hasParent) return true;
        const formattedString = parentName ? `${parentName}.${str}` : str;
        const parentString = formattedString.split('.').slice(0, -1).join('.');
        const parent = fields.find((field) => field.fieldName === parentString);
        return Boolean(parent) && parent?.fieldType === FieldType.OBJECT;
      }, 'One or more parent fields in the name do not exist. Create parent fields before adding child fields.')
      .refine((str) => !str.startsWith('_'), 'Field cannot start with an underscore')
      .refine((str) => !str.includes(','), 'Field cannot contain a comma')
      .refine((str) => !str.includes('#'), 'Field cannot contain a hash')
      .refine((str) => !Number(str), 'Field cannot contain only numbers')
      .refine((str) => !str.endsWith('.'), 'Field cannot end with a period'),
    fieldType: z.nativeEnum(FieldType),
    sampleValue: z.string().max(100, 'Sample value must be 100 characters or less'),
  });
};

type MappingFieldFormProviderProps = {
  children: ReactNode;
  /** Field provided when editing, to get current value(s) */
  field?: Mapping;
  /** Fields to check against for uniqueness and object type with `fieldName` and `fieldType` */
  fields: SchemaField[];
  parentName?: string;
};

export const MappingFieldFormProvider = ({
  children,
  fields,
  parentName,
}: MappingFieldFormProviderProps) => {
  const schema = createSchema(fields, parentName);

  const defaultValues = {
    description: '',
    fieldName: '',
    fieldType: FieldType.STRING,
    sampleValue: '',
  };

  const methods = useForm({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
